import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { selectUserUid } from 'src/app/core/store/user/user.selectors';
import { VanService } from '../services/van.service';
import * as VanActions from './van.actions';

@Injectable()
export class VanEffects {
  constructor(
    private actions$: Actions,
    private vanService: VanService,
    private store: Store
  ) {}

  // Load User Vans
  loadUserVans$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VanActions.loadVans),
      withLatestFrom(this.store.select(selectUserUid)),
      mergeMap(([_, userId]) => {
        if (!userId) {
          return of(
            VanActions.loadVansFailure({ error: 'User not authenticated' })
          );
        }
        return this.vanService.getUserVans(userId).pipe(
          map((vans) => VanActions.loadVansSuccess({ vans })),
          catchError((error) =>
            of(VanActions.loadVansFailure({ error: error.message }))
          )
        );
      })
    )
  );

  // Load Van
  loadVan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VanActions.loadVan),
      withLatestFrom(this.store.select(selectUserUid)),
      mergeMap(([{ vanId }, userId]) => {
        if (!userId) {
          return of(
            VanActions.loadVanFailure({ error: 'User not authenticated' })
          );
        }
        return this.vanService.getVan(userId, vanId).pipe(
          map((van) => {
            if (!van) {
              throw new Error('Van not found');
            }
            return VanActions.loadVanSuccess({ van });
          }),
          catchError((error) =>
            of(VanActions.loadVanFailure({ error: error.message }))
          )
        );
      })
    )
  );

  // Create Van
  createVan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VanActions.createVan),
      withLatestFrom(this.store.select(selectUserUid)),
      mergeMap(([{ vanData }, userId]) => {
        if (!userId) {
          return of(
            VanActions.createVanFailure({ error: 'User not authenticated' })
          );
        }
        return this.vanService.createVan(userId, vanData).pipe(
          map((van) => VanActions.createVanSuccess({ van })),
          catchError((error) =>
            of(VanActions.createVanFailure({ error: error.message }))
          )
        );
      })
    )
  );

  // Update Van
  updateVan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VanActions.updateVan),
      withLatestFrom(this.store.select(selectUserUid)),
      mergeMap(([{ vanId, updates }, userId]) => {
        if (!userId) {
          return of(
            VanActions.updateVanFailure({ error: 'User not authenticated' })
          );
        }

        return this.vanService.updateVan(userId, vanId, updates).pipe(
          mergeMap(() =>
            this.vanService.getVan(userId, vanId).pipe(
              map((van) => {
                if (!van) {
                  throw new Error('Van not found after update');
                }
                return VanActions.updateVanSuccess({ van });
              }),
              catchError((error) =>
                of(VanActions.updateVanFailure({ error: error.message }))
              )
            )
          ),
          catchError((error) =>
            of(VanActions.updateVanFailure({ error: error.message }))
          )
        );
      })
    )
  );

  // Delete Van
  deleteVan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VanActions.deleteVan),
      mergeMap(({ userId, vanId }) =>
        this.vanService.deleteVan(userId, vanId).pipe(
          map(() => VanActions.deleteVanSuccess({ vanId })),
          catchError((error) =>
            of(VanActions.deleteVanFailure({ error: error.message }))
          )
        )
      )
    )
  );

  // Archive Van
  archiveVan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VanActions.archiveVan),
      mergeMap(({ userId, vanId }) =>
        this.vanService.archiveVan(userId, vanId).pipe(
          mergeMap(() =>
            this.vanService.getVan(userId, vanId).pipe(
              map((van) => {
                if (!van) {
                  throw new Error('Van not found after archiving');
                }
                return VanActions.archiveVanSuccess({ van });
              }),
              catchError((error) =>
                of(VanActions.archiveVanFailure({ error: error.message }))
              )
            )
          )
        )
      )
    )
  );

  // Restore Van
  restoreVan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VanActions.restoreVan),
      mergeMap(({ userId, vanId }) =>
        this.vanService.restoreVan(userId, vanId).pipe(
          mergeMap(() =>
            this.vanService.getVan(userId, vanId).pipe(
              map((van) => {
                if (!van) {
                  throw new Error('Van not found after restoring');
                }
                return VanActions.restoreVanSuccess({ van });
              }),
              catchError((error) =>
                of(VanActions.restoreVanFailure({ error: error.message }))
              )
            )
          )
        )
      )
    )
  );

  // Share Van
  shareVan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VanActions.shareVan),
      mergeMap(({ userId, vanId, sharedUserId }) =>
        this.vanService.shareVan(userId, vanId, sharedUserId).pipe(
          mergeMap(() =>
            this.vanService.getVan(userId, vanId).pipe(
              map((van) => {
                if (!van) {
                  throw new Error('Van not found after sharing');
                }
                return VanActions.shareVanSuccess({ van });
              }),
              catchError((error) =>
                of(VanActions.shareVanFailure({ error: error.message }))
              )
            )
          )
        )
      )
    )
  );

  // Unshare Van
  unshareVan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VanActions.unshareVan),
      mergeMap(({ userId, vanId, sharedUserId }) =>
        this.vanService.unshareVan(userId, vanId, sharedUserId).pipe(
          mergeMap(() =>
            this.vanService.getVan(userId, vanId).pipe(
              map((van) => {
                if (!van) {
                  throw new Error('Van not found after unsharing');
                }
                return VanActions.unshareVanSuccess({ van });
              }),
              catchError((error) =>
                of(VanActions.unshareVanFailure({ error: error.message }))
              )
            )
          )
        )
      )
    )
  );
}
